import {
  BrainIcon,
  CalendarCheckIcon,
  MegaphoneIcon,
  ChartLineUpIcon,
  PaperPlaneTiltIcon,
  CreditCardIcon,
  DatabaseIcon,
  FlowArrowIcon,
  GaugeIcon,
  GearSixIcon,
  HouseLineIcon,
  LightbulbIcon,
  ListMagnifyingGlassIcon,
  PlugsConnectedIcon,
  ChatTeardropDotsIcon,
  ScrollIcon,
  StarIcon,
  StorefrontIcon,
  TagIcon,
  UsersThreeIcon,
  type Icon,
} from '@phosphor-icons/react';
import type { FeatureKey } from '@growhero/shared';

/**
 * Navigation config — the ONLY place feature agents touch to add/rename sidebar entries.
 * Labels are i18n keys in the `nav` namespace. `feature` marks Pro-gated items: they stay
 * visible for Basic tenants with a "Pro" mark (upsell surface) and their pages render <ProLock>.
 */
export interface NavItem {
  id: string;
  to: string;
  labelKey: string;
  icon: Icon;
  /** Feature required to use the page (absent = available on every plan). */
  feature?: FeatureKey;
  /** Match child routes as active (default true except for "/"). */
  end?: boolean;
  /**
   * Product area (ARCHITECTURE §11 "one colour per product area"): tints the nav icon so people
   * learn where they are. blue = AI & data, saffron = reviews, sea-teal = messages & customers.
   * Absent = neutral (home, billing, settings, admin).
   */
  area?: NavArea;
}

export type NavArea = 'ai' | 'reviews' | 'messages';

export interface NavGroup {
  id: string;
  labelKey?: string;
  items: NavItem[];
}

export const portalNav: NavGroup[] = [
  {
    id: 'main',
    items: [{ id: 'home', to: '/', labelKey: 'home', icon: HouseLineIcon, end: true }],
  },
  {
    id: 'customers',
    labelKey: 'groups.customers',
    items: [
      {
        id: 'contacts',
        to: '/contacts',
        labelKey: 'contacts',
        icon: UsersThreeIcon,
        area: 'messages',
      },
      { id: 'reviews', to: '/reviews', labelKey: 'reviews', icon: StarIcon, area: 'reviews' },
      {
        id: 'messages',
        to: '/messages',
        labelKey: 'messages',
        icon: PaperPlaneTiltIcon,
        area: 'messages',
      },
      {
        id: 'interest',
        to: '/interest',
        labelKey: 'interest',
        icon: MegaphoneIcon,
        feature: 'forms.interest',
        area: 'messages',
      },
      {
        id: 'events',
        to: '/events',
        labelKey: 'events',
        icon: CalendarCheckIcon,
        feature: 'forms.events',
        area: 'messages',
      },
    ],
  },
  {
    id: 'growth',
    labelKey: 'groups.growth',
    items: [
      {
        id: 'segments',
        to: '/segments',
        labelKey: 'segments',
        icon: TagIcon,
        feature: 'segments',
        area: 'messages',
      },
      {
        id: 'automations',
        to: '/automations',
        labelKey: 'automations',
        icon: FlowArrowIcon,
        feature: 'automations',
        area: 'messages',
      },
      {
        id: 'insights',
        to: '/insights',
        labelKey: 'insights',
        icon: LightbulbIcon,
        feature: 'insights',
        area: 'ai',
      },
      {
        id: 'assistant',
        to: '/assistant',
        labelKey: 'assistant',
        icon: ChatTeardropDotsIcon,
        feature: 'ai.assistant',
        area: 'ai',
      },
      {
        id: 'data',
        to: '/data',
        labelKey: 'data',
        icon: DatabaseIcon,
        feature: 'schema.custom',
        area: 'ai',
      },
    ],
  },
  {
    id: 'account',
    labelKey: 'groups.account',
    items: [
      { id: 'billing', to: '/billing', labelKey: 'billing', icon: CreditCardIcon },
      { id: 'settings', to: '/settings', labelKey: 'settings', icon: GearSixIcon },
    ],
  },
];

export const adminNav: NavGroup[] = [
  {
    id: 'platform',
    labelKey: 'groups.platform',
    items: [
      {
        id: 'overview',
        to: '/platform-admin',
        labelKey: 'admin.overview',
        icon: GaugeIcon,
        end: true,
      },
      {
        id: 'tenants',
        to: '/platform-admin/tenants',
        labelKey: 'admin.tenants',
        icon: StorefrontIcon,
      },
      { id: 'plans', to: '/platform-admin/plans', labelKey: 'admin.plans', icon: ChartLineUpIcon },
    ],
  },
  {
    id: 'ops',
    labelKey: 'groups.operations',
    items: [
      { id: 'models', to: '/platform-admin/models', labelKey: 'admin.models', icon: BrainIcon },
      {
        id: 'providers',
        to: '/platform-admin/providers',
        labelKey: 'admin.providers',
        icon: PlugsConnectedIcon,
      },
      { id: 'audit', to: '/platform-admin/audit', labelKey: 'admin.audit', icon: ScrollIcon },
    ],
  },
];

/** Search helper icon re-export for the command palette (future). */
export const SearchIcon = ListMagnifyingGlassIcon;
